import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../../../components/layout/Layout';
import {
  HelpCircle, ChevronDown, ChevronUp, Trophy, MessageSquare
} from 'lucide-react';

import BestClientsFAQ from '../components/BestClientsFAQ';
import HelpBotCard from '../components/HelpBotCard';
import { BirthdayFAQs } from '../components/BirthdayFAQs';
import { BusinessFAQs } from '../components/BusinessFAQs';
import { ClientsListFAQs } from '../components/ClientsListFAQs';
import { LatestRegistrationsFAQs } from '../components/LatestRegistrationsFAQs'; 
import { InactiveClientsFAQs } from '../components/InactiveClientsFAQs';
import { RewardsManagementFAQs } from '../components/RewardsManagementFAQs';
import { GiftCardsFAQs } from '../components/GiftCardsFAQs';

interface FAQItem {
  id: string;
  question: string;
  answer: React.ReactNode;
}

interface FAQCategory {
  id: string;
  title: string;
  icon: React.ReactNode;
  description: string;
  faqs: FAQItem[];
}

const FAQPage: React.FC = () => {
  const navigate = useNavigate();
  const [activeCategory, setActiveCategory] = useState<string | null>('business');
  const [expandedQuestions, setExpandedQuestions] = useState<Set<string>>(new Set());

  const toggleQuestion = useCallback((questionId: string) => {
    setExpandedQuestions(prev => {
      const newSet = new Set(prev);
      if (newSet.has(questionId)) newSet.delete(questionId);
      else newSet.add(questionId);
      return newSet;
    });
  }, []);

  const navigateTo = useCallback((path: string) => {
    navigate(path);
  }, [navigate]);

  const toggleCategory = useCallback((categoryId: string) => {
    setActiveCategory(prev => (prev === categoryId ? null : categoryId));
    setExpandedQuestions(new Set()); 
  }, []);

  const birthdayFAQData = BirthdayFAQs(navigateTo); 
  const businessFAQData = BusinessFAQs(navigateTo);
  const clientsListFAQData = ClientsListFAQs(navigateTo);
  const latestRegistrationsFAQData = LatestRegistrationsFAQs(navigateTo);
  const inactiveClientsFAQData = InactiveClientsFAQs(navigateTo);
  const rewardsManagementFAQData = RewardsManagementFAQs(navigateTo);
  const giftCardsFAQData = GiftCardsFAQs(navigateTo);

  const faqCategories: FAQCategory[] = [
    {
      id: businessFAQData.id,
      title: businessFAQData.title,
      icon: businessFAQData.icon,
      description: businessFAQData.description,
      faqs: businessFAQData.faqs,
    },
    {
      id: clientsListFAQData.id, 
      title: clientsListFAQData.title,
      icon: clientsListFAQData.icon, 
      description: clientsListFAQData.description,
      faqs: clientsListFAQData.faqs, 
    },
    {
      id: latestRegistrationsFAQData.id, 
      title: latestRegistrationsFAQData.title,
      icon: latestRegistrationsFAQData.icon, 
      description: latestRegistrationsFAQData.description,
      faqs: latestRegistrationsFAQData.faqs, 
    },
    {
      id: birthdayFAQData.id,
      title: birthdayFAQData.title,
      icon: birthdayFAQData.icon,
      description: birthdayFAQData.description,
      faqs: birthdayFAQData.faqs,
    },
    {
      id: inactiveClientsFAQData.id,
      title: inactiveClientsFAQData.title,
      icon: inactiveClientsFAQData.icon,
      description: inactiveClientsFAQData.description,
      faqs: inactiveClientsFAQData.faqs,
    },
    {
      id: rewardsManagementFAQData.id,
      title: rewardsManagementFAQData.title,
      icon: rewardsManagementFAQData.icon,
      description: rewardsManagementFAQData.description,
      faqs: rewardsManagementFAQData.faqs,
    },
    {
      id: giftCardsFAQData.id,
      title: giftCardsFAQData.title,
      icon: giftCardsFAQData.icon,
      description: giftCardsFAQData.description,
      faqs: giftCardsFAQData.faqs,
    },
    {
      id: 'best-clients',
      title: 'Mejores Clientes',
      icon: <Trophy className="h-5 w-5 text-yellow-500" />,
      description: 'Identificación y gestión de tus clientes más valiosos',
      faqs: [] 
    },
  ];

  return (
    <Layout>
      <div className="space-y-8 w-full">
        {/* Header */}
        <div className="animate-fade-in-up" style={{ animationDelay: '0.1s' }}>
          <h1 className="text-2xl font-bold text-content flex items-center">
            <HelpCircle className="h-6 w-6 mr-2 text-accent" />
            Preguntas Frecuentes
          </h1>
          <p className="mt-1 text-sm text-content-2">
            Encuentra respuestas a las preguntas más comunes sobre cómo usar el sistema
          </p>
        </div>

        {/* Asistente de ayuda (bot) — vive en el FAQ, como el ForoBot del foro */}
        <div className="animate-fade-in-up" style={{ animationDelay: '0.12s' }}>
          <HelpBotCard />
        </div>

        {/* Main content with categories and questions */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
          {/* Category selector - Sidebar */}
          <div className="md:col-span-3 animate-fade-in-up" style={{ animationDelay: '0.15s' }}>
            <div className="bg-surface rounded-lg shadow overflow-hidden">
              <div className="p-4 bg-blue-50 dark:bg-blue-500/10">
                <h2 className="font-medium text-blue-800 dark:text-blue-300">Categorías</h2>
              </div>
              <nav className="space-y-1 p-2">
                {faqCategories.map((category) => (
                  <button
                    key={category.id}
                    className={`w-full flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                      activeCategory === category.id
                        ? 'bg-blue-50 dark:bg-blue-500/10 text-blue-700 dark:text-blue-300'
                        : 'text-content-2 hover:bg-surface-2 hover:text-content'
                    }`}
                    onClick={() => toggleCategory(category.id)}
                  >
                    {category.icon}
                    <span className="ml-3">{category.title}</span>
                  </button>
                ))}
              </nav>
            </div>
          </div>

          {/* FAQ Content - Main area */}
          <div className="md:col-span-9 space-y-6">
            {activeCategory ? (
              <div className="animate-fade-in">
                {faqCategories
                  .filter((category) => category.id === activeCategory)
                  .map((category) => (
                    <div key={category.id}>
                      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-500/10 dark:to-indigo-500/10 p-4 rounded-lg shadow-sm animate-fade-in-up" style={{ animationDelay: '0.15s' }}>
                        <h2 className="text-xl font-bold text-content flex items-center">
                          {category.icon}
                          <span className="ml-2">{category.title}</span>
                        </h2>
                        <p className="mt-1 text-sm text-content-2">{category.description}</p>
                      </div>

                      <div className="mt-6 space-y-4">
                        {category.id === 'best-clients' ? ( 
                          <BestClientsFAQ onNavigate={navigateTo} />
                        ) : (
                          category.faqs.map((faq, index) => (
                            <div
                              key={faq.id}
                              className={`bg-surface rounded-lg shadow-sm overflow-hidden transition-all duration-200 animate-fade-in-up ${
                                expandedQuestions.has(faq.id) ? 'shadow-md' : ''
                              }`}
                              style={{ animationDelay: `${0.4 + index * 0.1}s` }}
                            >
                              <button
                                className="w-full flex justify-between items-start p-4 focus:outline-none"
                                onClick={() => toggleQuestion(faq.id)}
                              >
                                <span className="text-left font-medium text-content">{faq.question}</span>
                                <span className="ml-6 flex-shrink-0">
                                  {expandedQuestions.has(faq.id) ? (
                                    <ChevronUp className="h-5 w-5 text-content-3" />
                                  ) : (
                                    <ChevronDown className="h-5 w-5 text-content-3" />
                                  )}
                                </span>
                              </button>
                              {expandedQuestions.has(faq.id) && (
                                <div className="px-4 pb-4 text-content-2">
                                  <div className="border-t border-line pt-4">
                                    {faq.answer}
                                  </div>
                                </div>
                              )}
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                  ))}
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                {faqCategories.map((category, index) => (
                  <div
                    key={category.id}
                    className="bg-surface rounded-lg shadow-sm overflow-hidden hover:shadow-md transition-all duration-200 cursor-pointer animate-fade-in-up"
                    style={{ animationDelay: `${0.3 + index * 0.1}s` }}
                    onClick={() => toggleCategory(category.id)}
                  >
                    <div className="p-5">
                      <div className="flex items-center">
                        {category.icon}
                        <h3 className="ml-2 text-lg font-medium text-content">{category.title}</h3>
                      </div>
                      <p className="mt-2 text-sm text-content-3">{category.description}</p>
                      <div className="mt-4 flex items-center text-sm font-medium text-accent">
                        Ver preguntas
                        <ChevronDown className="ml-1 h-4 w-4" />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="bg-gradient-to-r from-indigo-50 to-blue-50 dark:from-indigo-500/10 dark:to-blue-500/10 rounded-lg shadow-sm p-6 mt-8 animate-fade-in-up" style={{ animationDelay: '0.15s' }}>
          <div className="sm:flex sm:items-center sm:justify-between">
            <div>
              <h3 className="text-lg font-medium text-content">¿No encuentras lo que buscas?</h3>
              <p className="mt-1 text-sm text-content-2">
                Contáctanos por WhatsApp para resolver cualquier duda adicional.
              </p>
            </div>
            <div className="mt-4 sm:mt-0">
              <a
                href="https://wa.me/526861326204?text=Hola%2C%20vengo%20de%20Lealify%20y%20necesito%20ayuda%20con%20mi%20cuenta."
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent"
              >
                <MessageSquare className="h-5 w-5 mr-2" />
                Contactar Soporte
              </a>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default FAQPage;